import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import loading from '../../assets/images/loading.gif'
import { BookingService } from '../../services/booking.service'
import { toast } from 'react-toastify'
import { StripeProvider } from '../../contexts/StripeContext'
import { PaymentForm } from '../../Components/PaymentForm'

const sendPostMessage = (delayTime: number = 200, status: string, session_id: string) => {
  if (window.parent && window.opener) {
    let origin = window.opener.location.href
    window.opener.postMessage({ status: status, session_id }, origin)
    origin = null
    setTimeout(() => {
      window.close()
    }, delayTime)
  }
}

const PaymentHandler = () => {
  const hasCalledRef = useRef(false)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [paymentData, setPaymentData] = useState<{
    clientSecret: string
    paymentIntentId: string
    booking: any
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (hasCalledRef.current) return
    const handlePayment = async () => {
      const session_id = searchParams.get('session_id')
      const status = searchParams.get('status')

      if (session_id && status) {
        sendPostMessage(2000, status, session_id)
      }

      const locations = searchParams.getAll('locations')
      const pickupDate = searchParams.get('pickupDate')
      const pickupTime = searchParams.get('pickupTime')
      const returnDate = searchParams.get('returnDate')
      const returnTime = searchParams.get('returnTime')
      const tripType = searchParams.get('tripType')
      const fleetId = searchParams.get('fleetId')
      const totalCostAmount = searchParams.get('totalCostAmount')
      const totalCostCurrencyCode = searchParams.get('totalCostCurrencyCode')

      // Extract booking info params
      const flightNumber = searchParams.get('flightNumber')
      const pickupSign = searchParams.get('pickupSign')
      const passengerCount = searchParams.get('passengerCount')
      const luggage = searchParams.get('luggage')
      const notes = searchParams.get('notes')
      const bookingForSomeoneElse = searchParams.get('bookingForSomeoneElse') === 'true'
      const guestFullName = searchParams.get('guestFullName')
      const guestEmail = searchParams.get('guestEmail')
      const guestPhoneNumber = searchParams.get('guestPhoneNumber')

      if (locations && pickupDate && pickupTime && fleetId) {
        // Construct trip segments
        const tripSegments = [
          {
            pickupAddress: locations[0],
            dropoffAddress: locations[1],
            scheduledAt: new Date(`${pickupDate}T${pickupTime}`).toISOString(),
            scheduledTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            flightName: flightNumber || undefined,
            notes: notes || undefined,
          },
        ]

        // Add return trip segment if it's a round trip
        if (tripType === 'return' && returnDate && returnTime) {
          tripSegments.push({
            pickupAddress: locations[1],
            dropoffAddress: locations[0],
            scheduledAt: new Date(`${returnDate}T${returnTime}`).toISOString(),
            scheduledTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            flightName: undefined,
            notes: undefined,
          })
        }

        const data = {
          vehicleId: fleetId,
          tripSegments,
          // Add booking-level fields
          passengerCount: passengerCount ? parseInt(passengerCount) : undefined,
          luggage: luggage || undefined,
          pickupSign: pickupSign || undefined,
          bookingForSomeoneElse: bookingForSomeoneElse,
          guestFullName: bookingForSomeoneElse ? guestFullName || undefined : undefined,
          guestEmail: bookingForSomeoneElse ? guestEmail || undefined : undefined,
          guestPhoneNumber: bookingForSomeoneElse ? guestPhoneNumber || undefined : undefined,
        }

        try {
          const bookingService = new BookingService()
          const booking = await bookingService.createBookingWithPayment(data)

          setPaymentData({
            clientSecret: booking.data.clientSecret,
            paymentIntentId: booking.data.paymentIntentId,
            booking: booking.data.booking,
          })
        } catch (error) {
          console.error('error ', error)
          setError('Failed to create booking, please go back and try again')
          toast.error('Failed to create booking, please go back and try again')
        }
      } else {
        setError('Missing required parameters, please go back and try again')
        toast.error('Missing required parameters, please go back and try again')
      }

      setIsLoading(false)
    }

    hasCalledRef.current = true
    handlePayment()
  }, [searchParams])

  const handlePaymentSuccess = async () => {
    setIsLoading(true)

    // wait 1s before navigating
    await new Promise(resolve => setTimeout(resolve, 1000))

    if (paymentData?.booking?.id) {
      // console.log('paymentData', paymentData)
      // console.log('paymentData.booking', paymentData?.booking)
      if (paymentData.booking.id) {
        sendPostMessage(2000, 'success', paymentData.booking.id)
        navigate(`/bookings?bookingId=${paymentData.booking.id}`)
      } else {
        navigate(`/bookings`)
      }
      // window.location.href = '/bookings'
    }
  }

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage)
    sendPostMessage(2000, 'failed', '')
  }

  if (isLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          height: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={loading}
          alt="loading"
          style={{
            width: '120px',
            height: '120px',
            background: '#fff',
            borderRadius: '50%',
          }}
        />
        <h3>Setting up your payment...</h3>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          height: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <h3 style={{ color: 'red', marginBottom: '20px' }}>Payment Error</h3>
        <p>{error}</p>
        <button
          onClick={() => window.history.back()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    )
  }

  if (!paymentData) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          height: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <h3>No payment data available</h3>
        <button
          onClick={() => window.history.back()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    )
  }

  return (
    <StripeProvider>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <PaymentForm
          clientSecret={paymentData.clientSecret}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      </div>
    </StripeProvider>
  )
}
export default PaymentHandler
